/**
 * Public church page calls to action: "Plan a Visit" and "Become a Member".
 *
 * Plan a Visit  → a Visitor record in the assembly's Visitors queue.
 * Become a Member → a pending Membership Application in the admin queue;
 *                   approving it creates the member record.
 *
 * Both are anonymous posts to /api/public/churches/{churchId}/...
 * When the same email already planned a visit, the backend links the two.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { CalendarHeart, HeartHandshake, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { getSource } from '@/lib/qrSource';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const post = async (path, body) => {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Something went wrong (${res.status})`);
  return data;
};

const useSourceOptions = (churchId) => {
  const [options, setOptions] = useState([]);
  React.useEffect(() => {
    if (!churchId) return;
    fetch(`${API_URL}/api/public/churches/${churchId}/visit-sources`)
      .then((r) => (r.ok ? r.json() : { options: [] }))
      .then((d) => setOptions(d.options || []))
      .catch(() => setOptions([]));
  }, [churchId]);
  return options;
};

const SourceSelect = ({ id, options, value, onChange, testId }) => {
  if (!options.length) return null;
  return (
    <Field id={id} label="How did you hear about us? (optional)">
      <select id={id} value={value} onChange={onChange} data-testid={testId}
              className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300">
        <option value="">Prefer not to say</option>
        {options.map((o) => (
          <option key={o.key} value={o.key}>{o.label}</option>
        ))}
      </select>
    </Field>
  );
};

const Field = ({ id, label, hint, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-slate-700">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
  </div>
);

const Done = ({ title, body, onClose }) => (
  <div className="py-8 text-center space-y-3" data-testid="cta-success">
    <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
    <p className="font-semibold text-slate-900">{title}</p>
    <p className="text-sm text-slate-600 max-w-sm mx-auto">{body}</p>
    <Button variant="outline" onClick={onClose} data-testid="cta-success-close">Close</Button>
  </div>
);

const PlanVisitDialogBase = ({ churchId, churchName, open, onOpenChange }) => {
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', preferred_service_date: '', message: '',
    heard_about: '',
  });
  const sources = useSourceOptions(churchId);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await post(`/api/public/churches/${churchId}/visit-request`,
        { ...form, source_code: getSource() });
      setDone(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setDone(false);
      setForm({ full_name: '', email: '', phone: '', preferred_service_date: '', message: '',
        heard_about: '' });
    }, 250);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg" data-testid="plan-visit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarHeart className="h-5 w-5 text-[var(--cp-primary,#4f46e5)]" />
            Plan a visit
          </DialogTitle>
          <DialogDescription>
            Tell us when you're coming and we'll be ready to welcome you to {churchName}.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <Done title="We're expecting you!"
                body="Someone from our welcome team will be in touch before your visit."
                onClose={close} />
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Field id="pv-name" label="Full name">
              <Input id="pv-name" required minLength={2} value={form.full_name}
                     onChange={set('full_name')} data-testid="plan-visit-name" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field id="pv-email" label="Email (optional)">
                <Input id="pv-email" type="email" value={form.email}
                       onChange={set('email')} data-testid="plan-visit-email" />
              </Field>
              <Field id="pv-phone" label="Phone / WhatsApp (optional)">
                <Input id="pv-phone" value={form.phone} onChange={set('phone')}
                       placeholder="+263 77 123 4567" data-testid="plan-visit-phone" />
              </Field>
            </div>
            <Field id="pv-date" label="Preferred service date (optional)">
              <Input id="pv-date" type="date" value={form.preferred_service_date}
                     onChange={set('preferred_service_date')} data-testid="plan-visit-date" />
            </Field>
            <SourceSelect id="pv-source" options={sources} value={form.heard_about}
                          onChange={set('heard_about')} testId="plan-visit-source" />
            <Field id="pv-message" label="Anything we should know? (optional)"
                   hint="Bringing children, needing directions, a prayer request — anything at all.">
              <Textarea id="pv-message" rows={3} value={form.message}
                        onChange={set('message')} data-testid="plan-visit-message" />
            </Field>
            <Button type="submit" className="w-full" disabled={busy}
                    data-testid="plan-visit-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {busy ? 'Sending…' : 'Let them know I\'m coming'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const BecomeMemberDialog = ({ churchId, churchName, open, onOpenChange }) => {
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', date_of_birth: '', address: '',
    previous_church: '', testimony: '', heard_about: '',
  });
  const sources = useSourceOptions(churchId);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error('Please accept the privacy notice so we can process your application.');
      return;
    }
    setBusy(true);
    try {
      await post(`/api/public/churches/${churchId}/membership-application`,
        { ...form, heard_about: form.heard_about || 'other', source_code: getSource(),
          consent: { accepted: true } });
      setDone(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setDone(false);
      setConsent(false);
      setForm({ full_name: '', email: '', phone: '', date_of_birth: '', address: '',
        previous_church: '', testimony: '', heard_about: '' });
    }, 250);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"
                     data-testid="become-member-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HeartHandshake className="h-5 w-5 text-[var(--cp-primary,#4f46e5)]" />
            Become a member
          </DialogTitle>
          <DialogDescription>
            Join the family at {churchName}. Our team reviews every application personally.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <Done title="Application received"
                body="A member of our team will review it and reach out to you shortly."
                onClose={close} />
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Field id="bm-name" label="Full name">
              <Input id="bm-name" required minLength={2} value={form.full_name}
                     onChange={set('full_name')} data-testid="become-member-name" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field id="bm-email" label="Email (optional)">
                <Input id="bm-email" type="email" value={form.email}
                       onChange={set('email')} data-testid="become-member-email" />
              </Field>
              <Field id="bm-phone" label="Phone / WhatsApp (optional)">
                <Input id="bm-phone" value={form.phone} onChange={set('phone')}
                       placeholder="+263 77 123 4567" data-testid="become-member-phone" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field id="bm-dob" label="Date of birth (optional)">
                <Input id="bm-dob" type="date" value={form.date_of_birth}
                       onChange={set('date_of_birth')} data-testid="become-member-dob" />
              </Field>
              <Field id="bm-prev" label="Previous church (optional)">
                <Input id="bm-prev" value={form.previous_church}
                       onChange={set('previous_church')} data-testid="become-member-previous" />
              </Field>
            </div>
            <Field id="bm-address" label="Home address (optional)">
              <Textarea id="bm-address" rows={2} value={form.address}
                        onChange={set('address')} data-testid="become-member-address" />
            </Field>
            <SourceSelect id="bm-source" options={sources} value={form.heard_about}
                          onChange={set('heard_about')} testId="become-member-source" />
            <Field id="bm-testimony" label="Your testimony (optional)"
                   hint="A few lines about your walk with God — it helps us welcome you well.">
              <Textarea id="bm-testimony" rows={4} value={form.testimony}
                        onChange={set('testimony')} data-testid="become-member-testimony" />
            </Field>
            <label className="flex items-start gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={consent} className="mt-0.5"
                     data-testid="become-member-consent"
                     onChange={(e) => setConsent(e.target.checked)} />
              I agree that {churchName} may store these details to review my application and,
              if approved, enrol me as a member. My information is encrypted at rest.
            </label>
            <Button type="submit" className="w-full" disabled={busy}
                    data-testid="become-member-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {busy ? 'Sending…' : 'Submit my application'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Exported so the assembly's welcome page — the one a QR code opens — can put
// its own full-width button in front of the same form, rather than a second
// copy of it existing somewhere else.
export const PlanVisitDialog = PlanVisitDialogBase;

export const ChurchCtaButtons = ({ churchId, churchName, variant = 'solid' }) => {
  const [visit, setVisit] = useState(false);
  const [member, setMember] = useState(false);
  if (!churchId) return null;

  const onDark = variant === 'onDark';
  const primary = onDark ? 'fwm-btn-ondark' : 'fwm-btn';
  const secondary = onDark ? 'fwm-btn-ondark' : 'fwm-btn-ghost';

  return (
    <div className="flex flex-wrap gap-3" data-testid="church-cta-buttons">
      <button onClick={() => setVisit(true)} data-testid="cta-plan-visit" className={primary}>
        <CalendarHeart className="h-4 w-4" /> Plan a Visit
      </button>
      <button onClick={() => setMember(true)} data-testid="cta-become-member" className={secondary}>
        <HeartHandshake className="h-4 w-4" /> Become a Member
      </button>

      <PlanVisitDialogBase churchId={churchId} churchName={churchName}
                       open={visit} onOpenChange={setVisit} />
      <BecomeMemberDialog churchId={churchId} churchName={churchName}
                          open={member} onOpenChange={setMember} />
    </div>
  );
};

export default ChurchCtaButtons;
